import React, { useState } from 'react';
import Head from 'next/head';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { GetServerSidePropsContext } from 'next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { AuthForm } from '@components/Form/styles';
import Preloader, { SIZES } from '@components/Preloader';
import WarningIcon from '@assets/images/icons/warning.svg';
import { GlobalError, GlobalErrorIcon } from '@styles/globalStyles';

import Input, { INPUT_TYPES } from '@components/Form/Input';
import Button, { BUTTON_TYPE } from '@components/Button';
import Link from '@components/Link';
import routes from '@constants/routes.json';
import { API, APIRoutes } from '@utils/api';

function SignInPage() {
  const [authError, setAuthError] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const { t } = useTranslation('passwordReset');

  const validationSchema = Yup.object().shape({
    email: Yup.string().email(t('email regex')).required(t('email required')),
  });

  const formOptions = { resolver: yupResolver(validationSchema) };
  const { handleSubmit, formState, control } = useForm(formOptions);
  const router = useRouter();

  const { errors } = formState;

  const { mutate: signin, isLoading } = useMutation((data) => API.post(APIRoutes.auth.password, { user: data }));

  const onSubmit = (formData) => {
    signin(formData, {
      onError: ({ response: { data } }) => {
        setAuthError(data.error);
        toast.error(data.error);
      },
      onSuccess: () => {
        setRedirecting(true);
        router.replace({ pathname: routes.homepage.source });
        toast.success(t("If your address exists in our database, we'll email you reset instructions."));
      },
    });
  };

  return (
    <>
      <Head>
        <title>{t('page-title')}</title>
      </Head>

      <AuthForm centered onSubmit={handleSubmit(onSubmit)}>
        {isLoading || redirecting ? <Preloader size={SIZES.LARGE} overlay /> : null}
        {authError ? (
          <GlobalError>
            <GlobalErrorIcon>
              <Image src={WarningIcon} alt="" />
            </GlobalErrorIcon>
            {authError}
          </GlobalError>
        ) : null}
        <Controller
          name="email"
          defaultValue=""
          control={control}
          render={({ field: { name, onChange, value } }) => (
            <Input
              name={name}
              inputType={INPUT_TYPES.TEXT}
              onChange={onChange}
              value={value}
              label={t('email')}
              error={authError ? { message: '' } : errors.email}
            />
          )}
        />
        <Button type={BUTTON_TYPE.SUBMIT} isLoading={isLoading}>
          {t('button')}
        </Button>
        <span>
          {t('question')} &nbsp;
          <Link href={routes.signup.source} replace>
            {t('signup')}
          </Link>
          <br />
          {t('question2')} &nbsp;
          <Link href={routes.signin.source} replace>
            {t('signin')}
          </Link>
        </span>
      </AuthForm>
    </>
  );
}

export async function getServerSideProps({ locale }: GetServerSidePropsContext) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['passwordReset'])),
    },
  };
}

export default SignInPage;
